import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  MessageSquare, Sparkles, BarChart3, CheckCircle2, Star,
  TrendingUp, Users, Lightbulb, ArrowRight, Lock, Trophy,
  Heart, Flame, Crown, Gift, Megaphone, Zap,
} from "lucide-react";
import OGMeta from "@/components/OGMeta";
import "@/styles/pulse.css";

const ICON_MAP = {
  Lightbulb, TrendingUp, Trophy, Sparkles, Users, MessageSquare,
  BarChart3, CheckCircle2, Star, Heart, Flame, Crown, Gift, Megaphone, Zap,
};
function Icon({ name, ...rest }) {
  const Cmp = ICON_MAP[name] || Lightbulb;
  return <Cmp {...rest} />;
}

function isExternalUrl(u) {
  return /^https?:\/\//i.test(u || "");
}

const TOTAL_KEY = "Total responses";

function ResultBar({ label, count, total, color = "#76B82A" }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="pulse-result-row" data-testid={`pulse-result-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="min-w-0">
        <div className="font-mono text-xs uppercase tracking-widest text-[#F5F2E8]/85 truncate">{label}</div>
        <div className="pulse-result-bar mt-1">
          <span style={{ width: `${pct}%`, background: color }} />
        </div>
      </div>
      <div className="font-display text-lg leading-none" style={{ color }}>
        {pct}<span className="text-[10px] text-[#F5F2E8]/55 ml-1">% · {count}</span>
      </div>
    </div>
  );
}

function QuestionResults({ q, results }) {
  if (!results) return null;
  const bucket = results.by_question?.[q.id];
  const total = results.total_responses || 0;
  if (!bucket || total === 0) {
    return <div className="font-mono text-xs text-[#F5F2E8]/45 mt-2">No responses yet.</div>;
  }
  if (q.type === "single" || q.type === "multi") {
    const counts = bucket.counts || {};
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const denom = q.type === "single"
      ? total
      : Math.max(1, entries.reduce((acc, [, c]) => acc + c, 0));
    return (
      <div className="mt-3 space-y-1">
        {entries.map(([label, c]) => (
          <ResultBar key={label} label={label} count={c} total={denom} />
        ))}
      </div>
    );
  }
  if (q.type === "rating") {
    const dist = bucket.distribution || {};
    return (
      <div className="mt-3 space-y-1">
        <div className="flex items-baseline gap-2 mb-2">
          <Star size={14} style={{ color: "#D7A62A" }} />
          <span className="font-display text-3xl" style={{ color: "#D7A62A" }}>{bucket.avg || 0}</span>
          <span className="font-mono text-xs text-[#F5F2E8]/55">avg · {bucket.count || 0} votes</span>
        </div>
        {[5, 4, 3, 2, 1].map((n) => (
          <ResultBar key={n} label={`${n} ★`} count={dist[String(n)] || 0} total={bucket.count || 1} color="#D7A62A" />
        ))}
      </div>
    );
  }
  if (q.type === "text") {
    const samples = bucket.samples || [];
    return (
      <div className="mt-3 space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/50">
          {bucket.count || 0} response{bucket.count === 1 ? "" : "s"} · most recent shown
        </div>
        {samples.length === 0 ? (
          <div className="font-mono text-xs text-[#F5F2E8]/45">No text yet.</div>
        ) : (
          samples.map((s, i) => (
            <div key={i} className="loot-card-flat p-3 text-sm italic text-[#F5F2E8]/75 border-l-2 border-[#76B82A]">
              "{s.text}"
            </div>
          ))
        )}
      </div>
    );
  }
  return null;
}

function QuestionInput({ q, value, onChange, disabled }) {
  if (q.type === "single") {
    return (
      <div className="space-y-2">
        {(q.options || []).map((opt) => (
          <button
            type="button"
            key={opt}
            disabled={disabled}
            onClick={() => onChange(opt)}
            className={`pulse-option w-full text-left ${value === opt ? "is-selected" : ""}`}
            data-testid={`pulse-opt-${q.id}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <span
              style={{
                width: 14, height: 14, borderRadius: "50%",
                border: `2px solid ${value === opt ? "#76B82A" : "rgba(245,242,232,0.4)"}`,
                background: value === opt ? "#76B82A" : "transparent",
                flexShrink: 0,
              }}
            />
            <span>{opt}</span>
          </button>
        ))}
      </div>
    );
  }
  if (q.type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      if (arr.includes(opt)) onChange(arr.filter((x) => x !== opt));
      else onChange([...arr, opt]);
    };
    return (
      <div className="space-y-2">
        {(q.options || []).map((opt) => {
          const on = arr.includes(opt);
          return (
            <button
              type="button"
              key={opt}
              disabled={disabled}
              onClick={() => toggle(opt)}
              className={`pulse-option w-full text-left ${on ? "is-selected" : ""}`}
              data-testid={`pulse-multi-${q.id}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <span
                style={{
                  width: 14, height: 14,
                  border: `2px solid ${on ? "#76B82A" : "rgba(245,242,232,0.4)"}`,
                  background: on ? "#76B82A" : "transparent",
                  flexShrink: 0,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}
              >
                {on && <CheckCircle2 size={10} color="#0F0F0F" />}
              </span>
              <span>{opt}</span>
            </button>
          );
        })}
      </div>
    );
  }
  if (q.type === "rating") {
    return (
      <div className="flex gap-2 items-center">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onChange(n)}
            className={`pulse-rating-btn ${value === n ? "is-active" : ""}`}
            data-testid={`pulse-rate-${q.id}-${n}`}
          >
            {n}
          </button>
        ))}
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 ml-3">
          1 = nope · 5 = love it
        </span>
      </div>
    );
  }
  if (q.type === "text") {
    return (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={3}
        maxLength={1000}
        placeholder="Type your thoughts here…"
        className="loot-input w-full"
        data-testid={`pulse-text-${q.id}`}
      />
    );
  }
  return null;
}

function SurveyCard({ survey, onSubmit }) {
  const { user } = useAuth();
  const [answers, setAnswers] = useState(() => survey.my_response?.answers || {});
  const [busy, setBusy] = useState(false);
  const hasVoted = !!survey.my_response;
  const [editing, setEditing] = useState(!hasVoted);
  const isClosed = survey.status === "closed";
  const canEdit = !isClosed && !!user;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pulse/${survey.id}/respond`, { answers });
      onSubmit?.(survey.id, answers, data?.results);
      setEditing(false);
      toast.success("Thanks! Your voice has been heard 👊");
      if (Array.isArray(data?.badges_granted) && data.badges_granted.length > 0) {
        // Celebrate every newly granted badge (usually just one — Voice of the Loot)
        setTimeout(() => {
          toast.success("🏆 New badge unlocked! Check your Badge Case.", { duration: 6000 });
        }, 600);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit response.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="loot-card-flat p-6 md:p-8" data-testid={`pulse-survey-${survey.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="overline inline-flex items-center gap-2">
            <BarChart3 size={12} />
            {isClosed ? "Closed survey" : "Active survey"}
            {hasVoted && (
              <span className="tag-pill text-[10px]" style={{ color: "#76B82A", borderColor: "#76B82A" }}>
                <CheckCircle2 size={10} className="inline mr-1" /> You voted
              </span>
            )}
          </div>
          <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight mt-2 leading-tight">
            {survey.title}
          </h2>
          {survey.description && (
            <p className="text-[#F5F2E8]/70 mt-2 leading-relaxed">{survey.description}</p>
          )}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/45 shrink-0">
          {survey.response_count} {survey.response_count === 1 ? "voice" : "voices"}
        </div>
      </div>

      {survey.why_asking && (editing || !hasVoted) && (
        <div
          className="mt-5 p-4 border-l-2 text-sm text-[#F5F2E8]/80 leading-relaxed"
          style={{ borderColor: "#D7A62A", background: "rgba(215,166,42,0.04)" }}
        >
          <div className="overline mb-1" style={{ color: "#D7A62A" }}>Why I'm asking</div>
          {survey.why_asking}
        </div>
      )}

      <div className="mt-6 space-y-5">
        {(survey.questions || []).map((q, idx) => (
          <div key={q.id} className="pulse-q-card">
            <div className="flex items-start gap-2 mb-3">
              <span
                className="font-mono text-[10px] uppercase tracking-widest text-[#76B82A] mt-1"
                style={{ minWidth: 30 }}
              >
                Q{idx + 1}
              </span>
              <div className="flex-1">
                <div className="font-display uppercase text-lg leading-tight">{q.label}</div>
                {q.required && (
                  <span className="font-mono text-[10px] text-[#F5F2E8]/40 uppercase">Required</span>
                )}
              </div>
            </div>
            {editing && canEdit ? (
              <QuestionInput
                q={q}
                value={answers[q.id]}
                onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
                disabled={busy}
              />
            ) : (
              <div className="space-y-2">
                {hasVoted && (
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 mb-1">
                    Your answer
                  </div>
                )}
                {hasVoted && (
                  <div className="text-sm text-[#F5F2E8]/85">
                    {Array.isArray(answers[q.id])
                      ? (answers[q.id] || []).join(", ") || "—"
                      : (answers[q.id] === undefined || answers[q.id] === ""
                        ? "—"
                        : String(answers[q.id]))}
                  </div>
                )}
                {survey.results && <QuestionResults q={q} results={survey.results} />}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Action row */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        {!user ? (
          <Link to="/login" className="btn-loot inline-flex items-center gap-2" data-testid={`pulse-login-${survey.id}`}>
            <Lock size={14} /> Sign in to vote
          </Link>
        ) : editing && canEdit ? (
          <>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="btn-loot inline-flex items-center gap-2"
              data-testid={`pulse-submit-${survey.id}`}
            >
              <Sparkles size={14} />
              {busy ? "Sending…" : (hasVoted ? "Update my answer" : "Send my answer")}
            </button>
            {hasVoted && (
              <button
                type="button"
                onClick={() => { setAnswers(survey.my_response?.answers || {}); setEditing(false); }}
                className="btn-ghost text-xs"
              >
                Cancel
              </button>
            )}
          </>
        ) : (
          canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="btn-ghost text-xs inline-flex items-center gap-1"
              data-testid={`pulse-edit-${survey.id}`}
            >
              Change my answer
            </button>
          )
        )}
        {isClosed && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/45">
            Voting closed — thanks to everyone who chimed in.
          </span>
        )}
      </div>
    </div>
  );
}

export default function PulsePage() {
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get("/pulse");
      setData(d);
    } catch {
      setData({ active: [], closed: [], faqs: [] });
    }
  };
  useEffect(() => { load(); }, []);

  const handleSubmit = (sid, answers, results) => {
    setData((prev) => {
      if (!prev) return prev;
      const patch = (list) => list.map((s) => s.id === sid
        ? { ...s, my_response: { answers, answered_at: new Date().toISOString() }, results: results || s.results, response_count: (s.response_count || 0) + (s.my_response ? 0 : 1) }
        : s);
      return { ...prev, active: patch(prev.active || []), closed: patch(prev.closed || []) };
    });
  };

  const stats = useMemo(() => {
    if (!data) return { active: 0, closed: 0, voices: 0 };
    const voices = [...(data.active || []), ...(data.closed || [])]
      .reduce((acc, s) => acc + (s.response_count || 0), 0);
    return {
      active: (data.active || []).length,
      closed: (data.closed || []).length,
      voices,
    };
  }, [data]);

  const DEFAULTS = {
    hero_eyebrow: "Community feedback",
    hero_title_pre: "Take the",
    hero_title_accent: "pulse.",
    hero_subtitle: "This app is built FOR you — not for me. Tell me what you want next, and I'll ship it. Real polls. Real votes. Real changes that come from real hunters.",
    stat_active_label: "Active polls",
    stat_closed_label: "Closed",
    stat_voices_label: "Voices heard",
    why_card_1_title: "Build what you want",
    why_card_1_body: "If you vote for more $20 drops over one big $500 drop, that's what gets built. No guesswork.",
    why_card_1_icon: "Lightbulb",
    why_card_2_title: "Grow the community",
    why_card_2_body: "Hard data on what hunters love helps us pitch the right sponsors, price tiers, and feature priorities.",
    why_card_2_icon: "TrendingUp",
    why_card_3_title: "Earn cred",
    why_card_3_body: "Vote on 3+ polls → unlock the legendary 'Voice of the Loot' badge. Future polls = more rare badges + early access.",
    why_card_3_icon: "Trophy",
    section_active_title_pre: "Drop your",
    section_active_title_accent: "vote.",
    section_active_hint: "close in your own time",
    empty_state_title: "No live polls right now.",
    empty_state_body: "Got a question you wish I'd ask? Tell me on the Holler.",
    section_closed_title: "Past polls.",
    faq_eyebrow: "Questions about the Pulse",
    faq_title: "FAQs.",
    cta_title: "Got a suggestion not on a poll?",
    cta_body: "Drop it in the Holler. If three hunters ask the same thing — it usually becomes the next poll.",
    cta_button_label: "Hit the Holler",
    cta_button_url: "/holler",
  };
  const S = { ...DEFAULTS, ...(data?.settings || {}) };

  return (
    <div className="py-8 space-y-10" data-testid="pulse-page">
      <OGMeta
        title="The Pulse · The Local Loot"
        description="Help shape The Local Loot. Vote on what gets built next."
      />

      {/* HERO */}
      <section className="pulse-hero">
        <div className="relative">
          <div className="overline inline-flex items-center gap-2" style={{ color: "#76B82A" }}>
            <span className="pulse-pulse-dot" /> {S.hero_eyebrow}
          </div>
          <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight mt-3 leading-[0.95]">
            {S.hero_title_pre} <span style={{ color: "#76B82A" }}>{S.hero_title_accent}</span>
          </h1>
          <p className="text-[#F5F2E8]/80 mt-5 max-w-2xl text-base md:text-lg leading-relaxed whitespace-pre-line">
            {S.hero_subtitle}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {[
              { k: S.stat_active_label, v: stats.active, color: "#76B82A", icon: <BarChart3 size={14} /> },
              { k: S.stat_closed_label, v: stats.closed, color: "#F5F2E8", icon: <CheckCircle2 size={14} /> },
              { k: S.stat_voices_label, v: stats.voices, color: "#D7A62A", icon: <Users size={14} /> },
            ].map((s) => (
              <div key={s.k} className="loot-card-flat p-3 px-4 flex items-center gap-2" data-testid={`pulse-stat-${(s.k || "").replace(/\s+/g, "-").toLowerCase()}`}>
                <span style={{ color: s.color }}>{s.icon}</span>
                <div>
                  <div className="font-display text-2xl leading-none" style={{ color: s.color }}>{s.v}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">{s.k}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY this exists */}
      <section className="grid md:grid-cols-3 gap-4">
        {[
          { i: S.why_card_1_icon, t: S.why_card_1_title, b: S.why_card_1_body },
          { i: S.why_card_2_icon, t: S.why_card_2_title, b: S.why_card_2_body },
          { i: S.why_card_3_icon, t: S.why_card_3_title, b: S.why_card_3_body },
        ].filter((c) => c.t || c.b).map((c, idx) => (
          <div key={idx} className="loot-card-flat p-5">
            <div className="flex items-center gap-2 text-[#76B82A]">
              <Icon name={c.i} size={20} />
              <span className="overline">Why</span>
            </div>
            <div className="font-display uppercase text-lg mt-2 leading-tight">{c.t}</div>
            <p className="text-sm text-[#F5F2E8]/70 mt-2 leading-relaxed whitespace-pre-line">{c.b}</p>
          </div>
        ))}
      </section>

      {/* ACTIVE SURVEYS */}
      <section className="space-y-6" data-testid="pulse-active-list">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <h2 className="font-display uppercase text-3xl tracking-tight">
            {S.section_active_title_pre} <span style={{ color: "#76B82A" }}>{S.section_active_title_accent}</span>
          </h2>
          <span className="font-mono text-xs text-[#F5F2E8]/50">
            {stats.active} open · {S.section_active_hint}
          </span>
        </div>
        {!data ? (
          <div className="font-mono text-sm text-[#F5F2E8]/55">Loading…</div>
        ) : (data.active || []).length === 0 ? (
          <div className="loot-card-flat p-10 text-center">
            <MessageSquare size={36} className="mx-auto text-[#F5F2E8]/25" />
            <div className="font-display uppercase text-2xl mt-3">{S.empty_state_title}</div>
            <p className="text-[#F5F2E8]/55 mt-2 text-sm whitespace-pre-line">{S.empty_state_body}</p>
          </div>
        ) : (
          data.active.map((s) => <SurveyCard key={s.id} survey={s} onSubmit={handleSubmit} />)
        )}
      </section>

      {/* CLOSED SURVEYS */}
      {data && (data.closed || []).length > 0 && (
        <section className="space-y-6">
          <h2 className="font-display uppercase text-2xl tracking-tight text-[#F5F2E8]/70">
            {S.section_closed_title}
          </h2>
          {data.closed.map((s) => <SurveyCard key={s.id} survey={s} onSubmit={handleSubmit} />)}
        </section>
      )}

      {/* FAQs */}
      <section data-testid="pulse-faqs">
        <div className="overline mb-2" style={{ color: "#D7A62A" }}>{S.faq_eyebrow}</div>
        <h2 className="font-display uppercase text-3xl tracking-tight mb-4">{S.faq_title}</h2>
        <div className="loot-card-flat px-5 md:px-8 py-2">
          {(data?.faqs || []).map((f, i) => (
            <details key={i} className="pulse-faq-item" data-testid={`pulse-faq-${i}`}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      {(S.cta_title || S.cta_body || S.cta_button_label) && (
        <section className="loot-card-flat p-8 md:p-10 text-center"
          style={{ background: "linear-gradient(135deg, rgba(118,184,42,0.10), rgba(15,15,15,1) 60%)", borderColor: "#76B82A" }}>
          <Users size={28} className="mx-auto text-[#76B82A]" />
          <div className="font-display uppercase text-2xl md:text-4xl tracking-tight mt-3">
            {S.cta_title}
          </div>
          {S.cta_body && (
            <p className="text-[#F5F2E8]/70 mt-2 max-w-lg mx-auto whitespace-pre-line">{S.cta_body}</p>
          )}
          {S.cta_button_label && S.cta_button_url && (
            isExternalUrl(S.cta_button_url) ? (
              <a
                href={S.cta_button_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-loot inline-flex items-center gap-2 mt-5"
                data-testid="pulse-cta-btn"
              >
                {S.cta_button_label} <ArrowRight size={14} />
              </a>
            ) : (
              <Link
                to={S.cta_button_url}
                className="btn-loot inline-flex items-center gap-2 mt-5"
                data-testid="pulse-cta-btn"
              >
                {S.cta_button_label} <ArrowRight size={14} />
              </Link>
            )
          )}
        </section>
      )}
    </div>
  );
}
